import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sparkles, ArrowRight, Calculator, Repeat } from "lucide-react";

/**
 * LandingDividendPreview — compact, always-live dividend income preview that
 * sits above the "Stop Guessing" band on the landing page. Uses the same
 * closed-form projection math as the full Calculator (mirrored here to avoid
 * pulling in the Calculator's 972-line file). Three tunable inputs keep the
 * cognitive load minimal for first-time visitors; the "Open the full
 * Dividend Calculator" CTA routes to /calculator for the deep dive.
 */

// Mirror of Calculator.project(), stripped to the fields this widget uses.
function project({ price, shares, years, yieldPct, priceGrowthPct, divGrowthPct, drip }) {
  const priceMult = 1 + priceGrowthPct / 100;
  const divMult = 1 + divGrowthPct / 100;
  let sh = shares;
  let px = price;
  let divPerShare = price * (yieldPct / 100);
  let totalDivs = 0;
  for (let y = 1; y <= years; y++) {
    px = px * priceMult;
    const grossDivs = sh * divPerShare;
    totalDivs += grossDivs;
    if (drip && px > 0) sh += grossDivs / px;
    divPerShare = divPerShare * divMult;
  }
  const finalValue = sh * px;
  const finalAnnualIncome = sh * divPerShare;
  const finalMonthlyIncome = finalAnnualIncome / 12;
  return { finalValue, finalAnnualIncome, finalMonthlyIncome, totalDivs };
}

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

// Preset chip options — mirror three "canonical" income ideas the landing
// wants visitors to recognize: broad-market blend (SCHD), high-yield ETF
// (JEPI), and a REIT (O). Numbers are indicative — full Calculator page
// pulls live quotes via yfinance.
const PRESETS = [
  { sym: "SCHD",  price: 27.5,  yieldPct: 3.6,  priceGrowth: 7,  divGrowth: 8 },
  { sym: "JEPI",  price: 56.5,  yieldPct: 7.5,  priceGrowth: 3,  divGrowth: 2 },
  { sym: "O",     price: 55.0,  yieldPct: 5.8,  priceGrowth: 3,  divGrowth: 3 },
];

export default function LandingDividendPreview() {
  const nav = useNavigate();
  const [investment, setInvestment] = useState(10000);
  const [yieldPct, setYieldPct] = useState(4.0);
  const [years, setYears] = useState(20);
  const [drip, setDrip] = useState(true);
  const [priceGrowth, setPriceGrowth] = useState(6);
  const [divGrowth, setDivGrowth] = useState(6);
  const [activePreset, setActivePreset] = useState(null);

  const price = 100; // notional — the "shares" derive from investment / price so the ratio is stable
  const shares = investment / price;

  const projection = useMemo(
    () =>
      project({
        price,
        shares,
        years,
        yieldPct,
        priceGrowthPct: priceGrowth,
        divGrowthPct: divGrowth,
        drip,
      }),
    [investment, years, yieldPct, priceGrowth, divGrowth, drip],
  );

  const applyPreset = (p) => {
    setActivePreset(p.sym);
    setYieldPct(p.yieldPct);
    setPriceGrowth(p.priceGrowth);
    setDivGrowth(p.divGrowth);
  };

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-10 lg:py-14"
      data-testid="landing-dividend-preview"
    >
      <div className="text-center max-w-3xl mx-auto">
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Live preview
        </div>
        <h2 className="serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
          See How Much <span className="italic text-secondary">Dividend Income</span> Your Portfolio Could Generate
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
          Drag the sliders, pick a preset, or open the full Dividend Calculator for tax, inflation, contributions & 30-year projections.
        </p>
      </div>

      <Card className="mt-8 overflow-hidden border-primary/20">
        <CardContent className="p-6 sm:p-8">
          <div className="grid gap-8 md:grid-cols-5">
            {/* --- Controls --- */}
            <div className="md:col-span-3 space-y-5">
              {/* Preset chips */}
              <div className="flex flex-wrap gap-2" data-testid="preview-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.sym}
                    type="button"
                    onClick={() => applyPreset(p)}
                    className={`px-3 py-1.5 rounded-full mono text-[10px] uppercase tracking-widest border transition-colors ${
                      activePreset === p.sym
                        ? "bg-secondary text-secondary-foreground border-secondary"
                        : "bg-card border-border text-muted-foreground hover:border-secondary/50 hover:text-secondary"
                    }`}
                    data-testid={`preview-preset-${p.sym}`}
                  >
                    {p.sym} · {p.yieldPct.toFixed(1)}%
                  </button>
                ))}
              </div>

              {/* Investment slider */}
              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Initial investment
                  </Label>
                  <span className="serif text-2xl tabular-nums">{fmt(investment)}</span>
                </div>
                <input
                  type="range"
                  min={1000}
                  max={250000}
                  step={1000}
                  value={investment}
                  onChange={(e) => setInvestment(Number(e.target.value))}
                  className="w-full mt-2 accent-secondary"
                  data-testid="preview-investment"
                />
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground flex justify-between mt-1">
                  <span>$1k</span><span>$250k</span>
                </div>
              </div>

              {/* Yield slider */}
              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Starting dividend yield
                  </Label>
                  <span className="serif text-2xl tabular-nums">{yieldPct.toFixed(1)}%</span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={10}
                  step={0.1}
                  value={yieldPct}
                  onChange={(e) => { setYieldPct(Number(e.target.value)); setActivePreset(null); }}
                  className="w-full mt-2 accent-secondary"
                  data-testid="preview-yield"
                />
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground flex justify-between mt-1">
                  <span>0.5%</span><span>10%</span>
                </div>
              </div>

              {/* Years slider */}
              <div>
                <div className="flex items-baseline justify-between">
                  <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Time horizon
                  </Label>
                  <span className="serif text-2xl tabular-nums">{years} yrs</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={40}
                  step={1}
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full mt-2 accent-secondary"
                  data-testid="preview-years"
                />
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground flex justify-between mt-1">
                  <span>1 yr</span><span>40 yrs</span>
                </div>
              </div>

              {/* DRIP toggle */}
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-4 py-3">
                <div>
                  <div className="text-sm font-medium inline-flex items-center gap-2">
                    <Repeat className="w-3.5 h-3.5 text-secondary" /> Reinvest dividends (DRIP)
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Compounds every payment back into more shares.</div>
                </div>
                <Switch checked={drip} onCheckedChange={setDrip} data-testid="preview-drip" />
              </div>
            </div>

            {/* --- Output stats --- */}
            <div className="md:col-span-2 flex flex-col gap-3">
              <div className="rounded-xl bg-gradient-to-br from-secondary/20 via-secondary/10 to-transparent border border-secondary/30 p-5">
                <div className="mono text-[10px] uppercase tracking-widest text-secondary">After {years} years</div>
                <div className="serif text-4xl sm:text-5xl mt-2 tabular-nums leading-none" data-testid="preview-final-monthly">
                  {fmt(projection.finalMonthlyIncome)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Projected monthly dividend income</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-border bg-card p-4">
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Annual income</div>
                  <div className="serif text-xl mt-1 tabular-nums" data-testid="preview-final-annual">
                    {fmt(projection.finalAnnualIncome)}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-4">
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Portfolio value</div>
                  <div className="serif text-xl mt-1 tabular-nums" data-testid="preview-final-value">
                    {fmt(projection.finalValue)}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-muted/20 p-4">
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Total dividends paid</div>
                <div className="serif text-xl mt-1 tabular-nums" data-testid="preview-total-divs">
                  {fmt(projection.totalDivs)}
                </div>
              </div>

              <Button
                onClick={() => nav("/calculator")}
                className="rounded-full mt-2"
                data-testid="preview-open-calculator"
              >
                <Calculator className="w-4 h-4 mr-2" /> Open the Full Dividend Calculator <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
              <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                Illustrative only — the full calculator adds tax, inflation, annual contributions, live quotes and Yield-on-Cost tables.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
